/**
 * Multiplayer tables: what the browser and the table's Durable Object say to each other over the
 * WebSocket, and the lobby rules both sides use. The server always re-checks everything.
 *
 *   POST /api/tables                  → { id, code }          create a private table (signed in)
 *   GET  /api/tables/code/<CODE>      → { id, code }          find a table by its room code (signed in, rate-limited)
 *   GET  /api/tables/<id>/ws          WebSocket               join / rejoin that table (signed in)
 *
 * Every message to the server carries a unique `id` (a retry reuses it: the original answer is sent
 * again and nothing happens twice) and `v`, the table version the browser last saw. A change made
 * against an out-of-date version is refused with a fresh copy of the table.
 */
import { PALETTE, colourDistance, distinct } from "../web/colours.ts";
import type { Move, SeatView, TableEvent } from "../engine/table.ts";
import type { StateMethod } from "../engine/whiskey.ts";

export const SEATS = 5;
export const MIN_HUMANS = 2;
/** AI players a table may have: the rest of the seats are kept for people. */
export const MAX_AI = SEATS - MIN_HUMANS;
export const aiCount = (seats: LobbySeat[]): number => seats.filter((s) => s?.kind === "ai").length;
export const MAX_MESSAGE_BYTES = 4096;
/** A table nobody is connected to closes by itself after this many days: a lobby, and a game in play. */
export const LOBBY_DAYS = 7, GAME_DAYS = 30;
/** The host can keep a table this many more days at a time, up to MAX_KEEP_DAYS ahead. */
export const EXTEND_DAYS = 5, MAX_KEEP_DAYS = 90;
/** The host is emailed once a table nobody is at gets down to this many days. */
export const REMIND_DAYS = 5;
/** Closed tables' rows (room code, host, who sat there) are deleted this many days after closing. */
export const PURGE_DAYS = 30;
/** A person who drops out of a game in play gets this long to come back; after that the AI makes their
 *  moves (only while they're away — the seat is theirs again the moment they reconnect). */
export const GRACE_MS = 45_000;
/** Room codes leave out look-alike characters (0/O, 1/I/L). */
export const CODE_ALPHABET = "ABCDEFGHJKMNPQRSTUVWXYZ23456789";
export const CODE_LENGTH = 6;
export const isCode = (s: string): boolean => new RegExp(`^[${CODE_ALPHABET}]{${CODE_LENGTH}}$`).test(s);

export const AI_SEAT_NAMES = ["Botty", "Steve", "Rhett", "Susanne", "Rob"];
export const AI_SEAT_PARTIES = ["Reform", "Progress", "Liberty", "Unity", "Green"];

export type TableStatus = "lobby" | "playing" | "finished" | "abandoned";

export interface LobbyHuman {
  kind: "human";
  playerId: string;
  name: string;
  avatarUrl: string | null;
  /** Their drawn avatar (JSON, as saved in the profile), shown when there's no photo. */
  avatarCfg?: string | null;
  party: string;
  /** The colour this player saved in their profile. */
  preferred: string;
  ready: boolean;
}
/** An AI seat: a real player's likeness (personas.ts) with their style, or (older tables) a plain AI. */
export interface LobbyAI { kind: "ai"; name: string; party: string; persona?: string; style?: string }
export type LobbySeat = LobbyHuman | LobbyAI | null;

/** The table as every member sees it (no private information exists before play starts). */
export interface PublicTable {
  id: string;
  code: string;
  status: TableStatus;
  version: number;
  hostId: string;
  /** How states are taken: the hand winner chooses, or states come up in random order. */
  method: StateMethod;
  /** When the table closes by itself if everyone leaves now (or stays away), ms. */
  closesAt: number;
  seats: (
    | (Omit<LobbyHuman, "preferred"> & { colour: string; preferred: string; connected: boolean })
    | (LobbyAI & { colour: string })
    | null
  )[];
  /** Why Start isn't possible yet (empty = it is). */
  blockers: string[];
}

export type ClientMessage =
  | { id: string; v: number; type: "ready"; ready: boolean }
  /** An AI player in that seat: the one chosen (`persona`, an AI player's id), or someone at random. */
  | { id: string; v: number; type: "addAI"; seat: number; persona?: string }
  | { id: string; v: number; type: "removeAI"; seat: number }
  | { id: string; v: number; type: "leave" }
  | { id: string; v: number; type: "start" }
  | { id: string; v: number; type: "takeover" }
  | { id: string; v: number; type: "ping" }
  | { id: string; v: number; type: "setMethod"; method: StateMethod }
  /** Your colour at this table (a palette colour or any colour from the wheel, not one too like another person's). Also saved as your colour. */
  | { id: string; v: number; type: "setColour"; colour: string }
  /** A move in the game (only ever your own seat's; the table checks it's yours and legal). */
  | { id: string; v: number; type: "move"; move: Move }
  /** You've finished looking at this hand's results. */
  | { id: string; v: number; type: "continue"; hand: number }
  /** Peek at the bones for this hand (PEEK_COST points, once; asking again shows it free). */
  | { id: string; v: number; type: "peek"; hand: number };

export type ServerMessage =
  | { type: "table"; table: PublicTable; you: { playerId: string; seat: number; host: boolean; controlling: boolean } }
  /** The answer to one message. `error` is a sentence for the player. */
  | { type: "ack"; id: string; ok: boolean; error?: string; stale?: boolean; retry?: boolean }
  /** The game as this seat may see it, and what just happened (only events this seat may see). */
  | { type: "game"; v: number; seat: number; view: SeatView; events: TableEvent[]; full: boolean;
      /** Between hands: when the next hand deals by itself, and who hasn't pressed Continue yet. */
      results: { hand: number; deadline: number; waiting: string[] } | null;
      /** Every person at the table is disconnected: the AI seats wait. */
      paused: boolean;
      /** People who are disconnected: from `coverAt` (ms) the AI makes their moves until they're back. */
      away: { seat: number; coverAt: number }[];
      /** The hand whose deal everyone hears "These cards are sticky!" (chosen once by the table, so every screen
       *  says it together), or null for none. */
      stickyHand: number | null }
  /** The connection is ending, and why (shown to the player). */
  | { type: "closing"; reason: string; code: number };

/** Check a message from a browser. Returns the message, or why it was refused. */
export function parseClientMessage(raw: unknown): ClientMessage | string {
  if (typeof raw !== "string") return "Messages must be text.";
  if (raw.length > MAX_MESSAGE_BYTES) return "That message is too large.";
  let m: Record<string, unknown>;
  try { m = JSON.parse(raw); } catch { return "That message isn't valid."; }
  if (!m || typeof m !== "object" || Array.isArray(m)) return "That message isn't valid.";
  if (typeof m.id !== "string" || !/^[A-Za-z0-9_-]{8,64}$/.test(m.id)) return "Every message needs an id.";
  if (!Number.isInteger(m.v) || (m.v as number) < 0) return "Every message needs the table version.";
  const seat = Number.isInteger(m.seat) && (m.seat as number) >= 0 && (m.seat as number) < SEATS;
  switch (m.type) {
    case "ready": return typeof m.ready === "boolean" ? (m as unknown as ClientMessage) : "Ready must be true or false.";
    case "addAI": if (m.persona !== undefined && (typeof m.persona !== "string" || !/^[a-z]{1,20}$/.test(m.persona))) return "Unknown AI player.";
      return seat ? (m as unknown as ClientMessage) : "Pick a seat from 1 to 5.";
    case "removeAI": return seat ? (m as unknown as ClientMessage) : "Pick a seat from 1 to 5.";
    case "leave": case "start": case "takeover": case "ping": return m as unknown as ClientMessage;
    case "setColour": return typeof m.colour === "string" && /^#[0-9a-f]{6}$/i.test(m.colour)
      ? ({ ...m, colour: (m.colour as string).toLowerCase() } as unknown as ClientMessage) : "Pick a colour.";
    case "setMethod": return m.method === "CHOOSE" || m.method === "RANDOM" ? (m as unknown as ClientMessage) : "Choose how states are taken.";
    case "continue": case "peek": return Number.isInteger(m.hand) ? (m as unknown as ClientMessage) : "Which hand?";
    case "move": {
      const mv = m.move as Record<string, unknown> | null;
      if (!mv || typeof mv !== "object" || !["action", "afterSwap", "knock", "state", "capital"].includes(mv.kind as string)) return "That move isn't valid.";
      return m as unknown as ClientMessage;     // the table checks the rest against the rules
    }
    default: return "Unknown message type.";
  }
}

/**
 * Table colours, seat by seat: each player keeps their saved colour unless someone seated before them
 * already has it (or one too like it); then they get the first palette colour that stands apart.
 * Nobody's saved preference is ever changed — this is only the colour for this table.
 */
export function tableColours(wanted: (string | null)[]): (string | null)[] {
  const used: string[] = [];
  const pool = PALETTE.map((p) => p.colour);
  return wanted.map((w) => {
    if (w === null) return null;
    const ok = (c: string) => used.every((u) => distinct(c, u));
    let c = ok(w) ? w : pool.find(ok);
    if (!c) c = pool.reduce((best, x) => (Math.min(...used.map((u) => colourDistance(x, u))) > Math.min(...used.map((u) => colourDistance(best, u))) ? x : best));
    used.push(c);
    return c;
  });
}

/** Everything that stops the host starting. */
export function startBlockers(seats: LobbySeat[], connected: (playerId: string) => boolean): string[] {
  const out: string[] = [];
  const empty = seats.filter((s) => s === null).length;
  const humans = seats.filter((s): s is LobbyHuman => s?.kind === "human");
  if (empty) out.push(`${empty} empty seat${empty > 1 ? "s" : ""} — ${aiCount(seats) >= MAX_AI ? `invite friends (${MAX_AI} AI players is the most a table can have)` : "invite friends or add AI players"}.`);
  if (humans.length < MIN_HUMANS) out.push(`At least ${MIN_HUMANS} people are needed (the rest can be AI).`);
  const away = humans.filter((h) => !connected(h.playerId));
  if (away.length) out.push(`Waiting for ${names(away)} to reconnect.`);
  const notReady = humans.filter((h) => !h.ready && connected(h.playerId));
  if (notReady.length) out.push(`Waiting for ${names(notReady)} to be ready.`);
  return out;
}
const names = (hs: LobbyHuman[]) => hs.length === 1 ? hs[0].name : `${hs.slice(0, -1).map((h) => h.name).join(", ")} and ${hs[hs.length - 1].name}`;
